import React, { useEffect, useMemo, useRef, useState } from 'react';
import './ReturnAttributionAnalysis.css';
import { useLocation, useNavigate } from 'react-router-dom';
import ConeChartSelection from '../../components/ConeChartSelection/ConeChartSelection';
import ChartSelection from '../../components/ChartSelection/ChartSelection';
import FactorSensitivityChart from '../../components/FactorSensitivityChart';
import RegressionChartSelection from '../../components/RegressionChartSelection/RegressionChartSelection';
import RegimeChart from '../../components/RegimeChart';
import DeleveragingEventsChart from '../../components/DeleveragingEventsChart';
import html2canvas from 'html2canvas';
import { saveAs } from 'file-saver';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  Container,
  IconButton,
  LinearProgress,
  Snackbar,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
import { API_BASE_URL } from '../../config/api';

const baseUrl = API_BASE_URL;

const createEmptyResults = () => ({
  Absolute: { 0: {}, 12: {}, 36: {}, 60: {} },
  Active: { 0: {}, 12: {}, 36: {}, 60: {} },
});

const REGIME_OPTIONS = {
  vix: {
    label: 'VIX',
    stageKey: 'vix_regime',
    metricKey: 'vix_regime',
    levelSeriesKey: 'vix_level_series',
    levelLabel: 'VIX level',
    subtitle: 'Month-end returns grouped by month-end VIX level.',
    scatterSubtitle: 'Month-end returns plotted against month-end VIX level with a fitted regression line.',
    emptyMessage: 'VIX regime analysis will appear once this stage finishes processing.',
  },
  dispersion: {
    label: 'Dispersion',
    stageKey: 'dispersion_regime',
    metricKey: 'dispersion_regime',
    levelSeriesKey: 'dispersion_level_series',
    levelLabel: 'Dispersion index level',
    subtitle: 'Month-end returns grouped by month-end CBOE S&P 500 Dispersion Index level.',
    scatterSubtitle:
      'Month-end returns plotted against month-end CBOE S&P 500 Dispersion Index level with a fitted regression line.',
    emptyMessage: 'Dispersion regime analysis will appear once this stage finishes processing.',
  },
};

const loadSubmittedComparisonData = () => {
  const rawValue = localStorage.getItem('submittedData') || localStorage.getItem('comparisonData');
  if (!rawValue) {
    return null;
  }

  try {
    return JSON.parse(rawValue);
  } catch {
    return null;
  }
};

const canvasToBlob = (canvas) => new Promise((resolve, reject) => {
  canvas.toBlob((blob) => {
    if (blob) {
      resolve(blob);
      return;
    }
    reject(new Error('Unable to create an image for this section.'));
  }, 'image/png');
});

const toSectionFilename = (title) => (
  `return-attribution-${title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')}.png`
);

const SectionImageActions = ({ busyMode, onCopy, onDownload }) => {
  const isBusy = Boolean(busyMode);

  return (
    <Box className="section-image-actions" data-section-image-control="true">
      <Tooltip title={busyMode === 'copy' ? 'Copying image...' : 'Copy section image'}>
        <span>
          <IconButton
            size="small"
            className="section-image-action-button"
            aria-label="Copy section image"
            onClick={onCopy}
            disabled={isBusy}
          >
            {busyMode === 'copy' ? (
              <CircularProgress size={16} thickness={5} />
            ) : (
              <ContentCopyRoundedIcon fontSize="small" />
            )}
          </IconButton>
        </span>
      </Tooltip>
      <Tooltip title={busyMode === 'download' ? 'Preparing download...' : 'Download section image'}>
        <span>
          <IconButton
            size="small"
            className="section-image-action-button"
            aria-label="Download section image"
            onClick={onDownload}
            disabled={isBusy}
          >
            {busyMode === 'download' ? (
              <CircularProgress size={16} thickness={5} />
            ) : (
              <DownloadRoundedIcon fontSize="small" />
            )}
          </IconButton>
        </span>
      </Tooltip>
    </Box>
  );
};

const AnalysisScreen = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const initialAnalysisId =
    location.state?.analysisId || localStorage.getItem('returnAttributionAnalysisId') || null;
  const initialData = location.state?.data || createEmptyResults();

  const [analysisId] = useState(initialAnalysisId);
  const [data, setData] = useState(initialData);
  const [status, setStatus] = useState(initialAnalysisId ? 'processing' : 'idle');
  const [progress, setProgress] = useState(null);
  const [error, setError] = useState('');
  const [selectedRegime, setSelectedRegime] = useState('vix');
  const [selectedRegimeType, setSelectedRegimeType] = useState('Active');
  const [selectedRegimeView, setSelectedRegimeView] = useState('regimes');
  const [captureState, setCaptureState] = useState({ sectionKey: null, mode: null });
  const [snackbarState, setSnackbarState] = useState({
    open: false,
    message: '',
    severity: 'success',
  });
  const sectionRefs = useRef({});
  const submittedComparisonData = useMemo(() => loadSubmittedComparisonData(), []);

  const parseApiJson = async (response, contextLabel) => {
    const rawText = await response.text();
    const trimmedText = rawText.trim();

    try {
      return trimmedText ? JSON.parse(trimmedText) : {};
    } catch {
      const looksLikeHtml =
        trimmedText.toLowerCase().startsWith('<!doctype') ||
        trimmedText.toLowerCase().startsWith('<html');
      if (looksLikeHtml) {
        throw new Error(
          `${contextLabel} returned HTML instead of JSON. Check API base URL/proxy target and backend port.`,
        );
      }
      throw new Error(`${contextLabel} returned invalid JSON.`);
    }
  };

  useEffect(() => {
    if (analysisId) {
      localStorage.setItem('returnAttributionAnalysisId', analysisId);
    }
  }, [analysisId]);

  useEffect(() => {
    if (!analysisId) {
      return undefined;
    }

    let isCancelled = false;
    let intervalId = null;

    const pollStatus = async () => {
      try {
        const response = await fetch(`${baseUrl}/api/return-attribution-status/${analysisId}`);
        const payload = await parseApiJson(response, 'Return attribution status API');
        if (!response.ok) {
          throw new Error(payload.error || `Status request failed (${response.status})`);
        }

        if (isCancelled) {
          return;
        }

        setData(payload.result || createEmptyResults());
        setProgress(payload.progress || null);

        const normalizedStatus = String(payload.status || '').toLowerCase() || 'processing';
        setStatus(normalizedStatus);
        setError(payload.error || '');

        if (normalizedStatus === 'completed' || normalizedStatus === 'error') {
          window.clearInterval(intervalId);
        }
      } catch (err) {
        if (isCancelled) {
          return;
        }
        setStatus('error');
        setError(err.message || 'Unable to fetch analysis status.');
        window.clearInterval(intervalId);
      }
    };

    pollStatus();
    intervalId = window.setInterval(pollStatus, 4000);

    return () => {
      isCancelled = true;
      if (intervalId) {
        window.clearInterval(intervalId);
      }
    };
  }, [analysisId]);

  const handleBackToEdit = () => {
    const basePath = window.location.pathname.startsWith('/insights') ? '/insights' : '';
    const targetPath = `${basePath}/manager-selection/tools/return-attribution`;

    navigate(targetPath);
  };

  const isWorking = status === 'processing' || status === 'pending' || status === 'started';
  const selectedRegimeConfig = REGIME_OPTIONS[selectedRegime];
  const regressionProgress = useMemo(() => ({
    completed: progress?.regression?.completed_windows ?? 0,
    total: progress?.regression?.total_windows ?? 0,
  }), [progress]);

  const handleRegimeChange = (event, nextRegime) => {
    void event;
    if (nextRegime !== null) {
      setSelectedRegime(nextRegime);
    }
  };

  const handleSnackbarClose = (_, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbarState((current) => ({ ...current, open: false }));
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbarState({
      open: true,
      message,
      severity,
    });
  };

  const setSectionRef = (sectionKey) => (node) => {
    if (node) {
      sectionRefs.current[sectionKey] = node;
      return;
    }
    delete sectionRefs.current[sectionKey];
  };

  const captureSectionCanvas = async (sectionKey) => {
    const sectionNode = sectionRefs.current[sectionKey];

    if (!sectionNode) {
      throw new Error('This section is not ready to capture yet.');
    }

    return html2canvas(sectionNode, {
      backgroundColor: '#ffffff',
      logging: false,
      scale: Math.min(window.devicePixelRatio || 1, 2),
      useCORS: true,
      ignoreElements: (element) => element?.dataset?.sectionImageControl === 'true',
    });
  };

  const handleSectionDownload = async (sectionKey, title) => {
    setCaptureState({ sectionKey, mode: 'download' });

    try {
      const canvas = await captureSectionCanvas(sectionKey);
      const imageBlob = await canvasToBlob(canvas);
      saveAs(imageBlob, toSectionFilename(title));
      showSnackbar(`${title} downloaded as a PNG.`, 'success');
    } catch (err) {
      showSnackbar(err.message || `Unable to download ${title}.`, 'error');
    } finally {
      setCaptureState({ sectionKey: null, mode: null });
    }
  };

  const handleSectionCopy = async (sectionKey, title) => {
    setCaptureState({ sectionKey, mode: 'copy' });

    try {
      if (!window.isSecureContext || !navigator.clipboard?.write || !window.ClipboardItem) {
        throw new Error('Image copy is not supported in this browser. Use download instead.');
      }

      const canvas = await captureSectionCanvas(sectionKey);
      const imageBlob = await canvasToBlob(canvas);
      await navigator.clipboard.write([
        new window.ClipboardItem({ [imageBlob.type]: imageBlob }),
      ]);
      showSnackbar(`${title} copied to your clipboard.`, 'success');
    } catch (err) {
      showSnackbar(err.message || `Unable to copy ${title}.`, 'error');
    } finally {
      setCaptureState({ sectionKey: null, mode: null });
    }
  };

  const renderSectionActions = (sectionKey, title) => (
    <SectionImageActions
      busyMode={captureState.sectionKey === sectionKey ? captureState.mode : null}
      onCopy={() => handleSectionCopy(sectionKey, title)}
      onDownload={() => handleSectionDownload(sectionKey, title)}
    />
  );

  return (
    <div className="analysis-screen">
      <Container className="analysis-screen-container">
        <div className="header-container">
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={handleBackToEdit}
            className="back-button"
          >
            Edit Inputs
          </Button>

          <Typography variant="h4" className="page-title">
            Analysis
          </Typography>
        </div>

        {!analysisId && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Submit data from the input page to start a return attribution analysis.
          </Alert>
        )}

        {analysisId && (
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 1, flexWrap: 'wrap' }}>
              <Chip label={`Analysis ID: ${analysisId}`} size="small" />
              <Chip
                label={isWorking ? 'Processing' : status === 'completed' ? 'Completed' : 'Error'}
                color={status === 'completed' ? 'success' : status === 'error' ? 'error' : 'warning'}
                size="small"
              />
              {isWorking && <CircularProgress size={16} />}
            </Box>
            {isWorking && <LinearProgress />}
            {error && (
              <Alert severity="error" sx={{ mt: 1 }}>
                {error}
              </Alert>
            )}
          </Box>
        )}

        <div className="charts-grid">
          <Card
            ref={setSectionRef('cone-chart')}
            className={`analysis-card${captureState.sectionKey === 'cone-chart' ? ' section-image-capturing' : ''}`}
          >
            <CardHeader
              title="Cone Chart"
              className="card-header"
              classes={{ title: 'card-header-title' }}
              action={renderSectionActions('cone-chart', 'Cone Chart')}
            />
            <CardContent className="card-content">
              <ConeChartSelection data={data} metric="cone_chart" />
            </CardContent>
          </Card>

          <Card
            ref={setSectionRef('rolling-metrics')}
            className={`analysis-card${captureState.sectionKey === 'rolling-metrics' ? ' section-image-capturing' : ''}`}
          >
            <CardHeader
              title="Rolling Metrics"
              className="card-header"
              classes={{ title: 'card-header-title' }}
              action={renderSectionActions('rolling-metrics', 'Rolling Metrics')}
            />
            <CardContent className="card-content">
              <ChartSelection data={data} />
            </CardContent>
          </Card>

          <Card
            ref={setSectionRef('regime-returns')}
            className={`analysis-card${captureState.sectionKey === 'regime-returns' ? ' section-image-capturing' : ''}`}
          >
            <CardHeader
              title="Regime Returns"
              className="card-header"
              classes={{ title: 'card-header-title' }}
              action={renderSectionActions('regime-returns', 'Regime Returns')}
            />
            <CardContent className="card-content">
              <RegimeChart
                data={data}
                metricKey={selectedRegimeConfig.metricKey}
                levelSeriesKey={selectedRegimeConfig.levelSeriesKey}
                levelLabel={selectedRegimeConfig.levelLabel}
                subtitle={selectedRegimeConfig.subtitle}
                scatterSubtitle={selectedRegimeConfig.scatterSubtitle}
                emptyMessage={selectedRegimeConfig.emptyMessage}
                selectedType={selectedRegimeType}
                onTypeChange={setSelectedRegimeType}
                selectedView={selectedRegimeView}
                onViewChange={setSelectedRegimeView}
                middleControl={(
                  <ToggleButtonGroup
                    value={selectedRegime}
                    exclusive
                    onChange={handleRegimeChange}
                    aria-label="Regime analysis selector"
                    size="small"
                  >
                    {Object.entries(REGIME_OPTIONS).map(([key, option]) => (
                      <ToggleButton key={key} value={key} aria-label={`${option.label} regime returns`}>
                        {option.label}
                      </ToggleButton>
                    ))}
                  </ToggleButtonGroup>
                )}
              />
            </CardContent>
          </Card>

          <Card
            ref={setSectionRef('deleveraging-event-returns')}
            className={`analysis-card${captureState.sectionKey === 'deleveraging-event-returns' ? ' section-image-capturing' : ''}`}
          >
            <CardHeader
              title="Deleveraging Event Returns"
              className="card-header"
              classes={{ title: 'card-header-title' }}
              action={renderSectionActions('deleveraging-event-returns', 'Deleveraging Event Returns')}
            />
            <CardContent className="card-content">
              <DeleveragingEventsChart data={data} />
            </CardContent>
          </Card>

          <Card
            ref={setSectionRef('factor-sensitivity')}
            className={`analysis-card${captureState.sectionKey === 'factor-sensitivity' ? ' section-image-capturing' : ''}`}
          >
            <CardHeader
              title="Factor Sensitivity"
              className="card-header"
              classes={{ title: 'card-header-title' }}
              action={renderSectionActions('factor-sensitivity', 'Factor Sensitivity')}
            />
            <CardContent className="card-content">
              <FactorSensitivityChart
                data={data}
                submittedData={submittedComparisonData}
                baseUrl={baseUrl}
              />
            </CardContent>
          </Card>

          <Card
            ref={setSectionRef('regression-models')}
            className={`analysis-card${captureState.sectionKey === 'regression-models' ? ' section-image-capturing' : ''}`}
          >
            <CardHeader
              title="Regression Models"
              className="card-header"
              classes={{ title: 'card-header-title' }}
              subheader={
                regressionProgress.total
                  ? `${regressionProgress.completed}/${regressionProgress.total} windows complete`
                  : undefined
              }
              action={renderSectionActions('regression-models', 'Regression Models')}
            />
            <CardContent className="card-content">
              <RegressionChartSelection data={data} metric="regression_metric" defaultModel="Lasso" />
            </CardContent>
          </Card>
        </div>

        <Snackbar
          open={snackbarState.open}
          autoHideDuration={4000}
          onClose={handleSnackbarClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert onClose={handleSnackbarClose} severity={snackbarState.severity} sx={{ width: '100%' }}>
            {snackbarState.message}
          </Alert>
        </Snackbar>
      </Container>
    </div>
  );
};

export default AnalysisScreen;
